import { LinkOutlined } from "@ant-design/icons";
import { useAuth } from "@entities/auth";
import { ICampaignAlterationApprovalRequest } from "@entities/campaigns/lib/types";
import { useGetAlterationRequests } from "@entities/financeApproval/lib/hooks/useGetAlterationRequests";
import { useGetFinanceApprovalLots } from "@entities/financeApproval/lib/hooks/useGetFinanceApprovalLots";
import AdminCampaignDetailsChangeModal from "@features/AdminCampaignDetailsChange";
import { LotView } from "@features/lotView/LotView";
import {
  IFinanceApprovalLot,
  purchaseLotStatus,
} from "@shared/lib/types/finance";
import { formatData } from "@shared/lib/utils/formatData";
import { Button, Input, Table } from "@shared/ui";
import { Image, Modal, Popover } from "antd";
import { ColumnsType } from "antd/es/table";
import { ClipboardCheck, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import styles from "./styles.module.scss";

export function FinanceApproval() {
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState<string>(
    searchParams.get("briefName") || ""
  );

  const { beUser } = useAuth();
  const [approvalTab, setApprovalTab] = useState<"payment" | "alteration">("payment");

  const [selectedLotStatus, setSelectedLotStatus] = useState<
    purchaseLotStatus | undefined
  >(undefined);

  const handleSearchChange = (val: string) => {
    setSearchQuery(val);
  };

  const [paegNo, setPageNo] = useState<number>(1);
  const [pageSize, setPageSize] = useState(10);

  // Hook for Payment Approval tab
  const { lots, totalCount, isFetching } = useGetFinanceApprovalLots({
    limit: pageSize,
    pageNo: paegNo,
    searchQuery,
    status: selectedLotStatus ? [selectedLotStatus] : undefined,
  }, approvalTab === "payment");

  // Hook for Alteration Approval tab
  const { alterations, totalCount: alterationTotalCount, isFetching: isAlterationsFetching } = useGetAlterationRequests({
    limit: pageSize,
    pageNo: paegNo,
    searchQuery,
    status: selectedLotStatus ? [selectedLotStatus] : [],
  }, approvalTab === "alteration");

  const [modalProps, setModalProps] = useState<{
    open: boolean;
    lotId: string;
    isView: boolean;
  }>({
    open: false,
    lotId: "",
    isView: false,
  });

  const [selectedAlteration, setSelectedAlteration] = useState<ICampaignAlterationApprovalRequest | null>(null);
  const [alterationModalOpen, setAlterationModalOpen] = useState(false);

  const handlePaginationChange = (page: number, size: number) => {
    setPageNo(page);
    setPageSize(size);
  };

  useEffect(() => {
    setPageNo(1);
  }, [pageSize, searchQuery, selectedLotStatus, approvalTab]);

  const getCreatorName = (record: ICampaignAlterationApprovalRequest) => {
    return record.influencerName || record.socialMediaName || '—';
  };

  const getCampaignName = (record: ICampaignAlterationApprovalRequest) => {
    return record.briefName || '—';
  };

  const money = (amount?: number) => `₹${formatData(amount || 0, "0,00.00")}`;

  const paymentColumns: ColumnsType<IFinanceApprovalLot> = [
    {
      title: "Campaign",
      dataIndex: "campaignName",
      key: "campaignInfo",
      width: 240,
      render: (_: string, record: IFinanceApprovalLot) => (
        <div className={styles.campaignCell}>
          <span className={styles.campaignName}>{record.brief?.name || "—"}</span>
          <span className={styles.campaignMeta}>
            {[record.brand?.name, record.lotNumber ? `Lot ${record.lotNumber}` : ""]
              .filter(Boolean)
              .join(" · ")}
          </span>
        </div>
      ),
    },
    {
      title: "Purchased by",
      dataIndex: "purchasedBy",
      key: "purchasedBy",
      width: 150,
      render: (_: string, record: IFinanceApprovalLot) => (
        <span className={styles.personName}>
          {[record.createdBy?.firstName, record.createdBy?.lastName]
            .filter(Boolean)
            .join(" ") || "—"}
        </span>
      ),
    },
    {
      title: "Influencer commercial",
      hidden: beUser?.demoMode,
      dataIndex: "influencerCost",
      key: "influencerCost",
      width: 160,
      render: (amount: number) => <span className={styles.money}>{money(amount)}</span>,
    },
    {
      title: "Service charge",
      dataIndex: "serviceCharge",
      key: "serviceCharge",
      width: 140,
      render: (amount: number) => <span className={styles.money}>{money(amount)}</span>,
    },
    {
      title: "Brand price",
      dataIndex: "brandCost",
      key: "brandCost",
      width: 130,
      render: (amount: number) => <span className={styles.money}>{money(amount)}</span>,
    },
    {
      title: "Revenue",
      dataIndex: "revenue",
      key: "revenue",
      width: 140,
      render: (_: number, record: IFinanceApprovalLot) => (
        <div className={styles.moneyCell}>
          <span className={styles.money}>{money(record.revenue)}</span>
          <span className={styles.moneyMeta}>
            {record.revenuePercentage.toFixed(2)}%
          </span>
        </div>
      ),
    },
    {
      title: "Payment Proof",
      dataIndex: "paymentProof",
      key: "paymentProof",
      render: (_: string, record: IFinanceApprovalLot) => (
        <Popover
          content={
            <Image
              src={record.purchaseProof || ""}
              alt="Purchase Proof"
              width={100}
            />
          }
        >
          <Button
            type="link"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              window.open(record.purchaseProof || "", "_blank");
            }}
            href={record.purchaseProof}
            icon={<LinkOutlined />}
          >
            Payment Proof
          </Button>
        </Popover>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 140,
      render: (status: purchaseLotStatus) => {
        const tone =
          status === purchaseLotStatus.Approved
            ? styles.statusApproved
            : status === purchaseLotStatus.Rejected
              ? styles.statusRejected
              : styles.statusPending;
        const label =
          status === purchaseLotStatus.Approved
            ? "Approved"
            : status === purchaseLotStatus.Rejected
              ? "Declined"
              : "Waiting";

        return <span className={`${styles.statusPill} ${tone}`}>{label}</span>;
      },
    },
    {
      title: "Action",
      key: "action",
      render: (_: any, record: IFinanceApprovalLot) => {
        const getButtonText = (status: purchaseLotStatus) => {
          switch (status) {
            case purchaseLotStatus.Rejected:
            case purchaseLotStatus.Approved:
              return "View lot";
            case purchaseLotStatus.Pending:
              return "Approve lot";
            default:
              return "View lot";
          }
        };

        return (
          <Button
            type="default"
            size="small"
            className={styles.actionBtn}
            onClick={() =>
              setModalProps({
                open: true,
                lotId: record._id,
                isView: getButtonText(record.status) === "View lot",
              })
            }
          >
            {getButtonText(record.status)}
          </Button>
        );
      },
    },
  ];

  const costChange = (direction: "add" | "subtract" | undefined, amount: number) => {
    const isAdd = direction === "add";
    return (
      <span className={isAdd ? styles.changeAdd : styles.changeSubtract}>
        {isAdd ? "+" : "−"} ₹{formatData(amount || 0, "0,00.00")}
      </span>
    );
  };

  const alterationColumns: ColumnsType<ICampaignAlterationApprovalRequest> = [
    {
      title: "Creator",
      key: "creatorCampaign",
      width: 220,
      render: (_value, record) => (
        <div className={styles.campaignCell}>
          <span className={styles.campaignName}>{getCreatorName(record)}</span>
          <span className={styles.campaignMeta}>{getCampaignName(record)}</span>
        </div>
      ),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: 180,
      render: (type: string) => (
        <span className={styles.typePill}>{type.replace(/-/g, " ")}</span>
      ),
    },
    {
      title: "Influencer change",
      key: "influencerChange",
      width: 160,
      render: (_value, record) =>
        record.toAlterInfluencerCost
          ? costChange(record.influencerCostDirection, record.influencerCostBaseCost || 0)
          : "—",
    },
    {
      title: "Brand change",
      key: "brandChange",
      width: 150,
      render: (_value, record) =>
        record.toAlterBrandCost
          ? costChange(record.brandCostDirection, record.brandCostBaseCost || 0)
          : "—",
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
      key: "remarks",
      width: 240,
      render: (remarks: string) => (
        <span className={styles.remarks} title={remarks}>
          {remarks || "—"}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 130,
      render: (status: string) => {
        const tone =
          status === "Approved"
            ? styles.statusApproved
            : status === "Rejected"
              ? styles.statusRejected
              : styles.statusPending;
        const label =
          status === "Approved"
            ? "Approved"
            : status === "Rejected"
              ? "Declined"
              : "Waiting";
        return <span className={`${styles.statusPill} ${tone}`}>{label}</span>;
      },
    },
    {
      title: "Action",
      key: "action",
      width: 100,
      render: (_value, record) => (
        <Button
          type="default"
          size="small"
          className={styles.actionBtn}
          onClick={() => {
            setSelectedAlteration(record);
            setAlterationModalOpen(true);
          }}
        >
          View
        </Button>
      ),
    },
  ];

  const matchingCount =
    (approvalTab === "payment" ? totalCount : alterationTotalCount) || 0;
  const statusFilters: { label: string; value?: purchaseLotStatus }[] = [
    { label: "All" },
    { label: "Waiting", value: purchaseLotStatus.Pending },
    { label: "Approved", value: purchaseLotStatus.Approved },
    { label: "Declined", value: purchaseLotStatus.Rejected },
  ];

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>Finance Approval</h1>
      <p className={styles.subtitle}>
        Review payment lots and campaign alterations. Use the tabs to switch
        lists and narrow by status.
      </p>
      <Table
        className={styles.table}
        dataSource={approvalTab === "payment" ? (lots || []) : (alterations || []) as any}
        columns={(approvalTab === "payment" ? paymentColumns : alterationColumns) as any}
        loading={approvalTab === "payment" ? isFetching : isAlterationsFetching}
        rowKey="_id"
        title={() => (
          <div>
            <div className={styles.header}>
              <div className={styles.identity}>
                <div className={styles.iconWrap}>
                  <ClipboardCheck size={18} />
                </div>
                <div>
                  <h2 className={styles.headerTitle}>
                    {approvalTab === "payment"
                      ? "Payment approvals"
                      : "Alteration approvals"}
                  </h2>
                  <p className={styles.meta}>{matchingCount} matching</p>
                </div>
              </div>
              <Input
                placeholder={
                  approvalTab === "payment"
                    ? "Search by influencer or campaign"
                    : "Search by creator or remarks"
                }
                value={searchQuery}
                onChange={(event) => handleSearchChange(event.target.value)}
                allowClear
                className={styles.searchInput}
                prefix={<Search size={14} />}
              />
            </div>
            <div className={styles.tabRow}>
              <nav className={styles.tabList} aria-label="Approval type">
                {(
                  [
                    ["payment", "Payment approvals"],
                    ["alteration", "Alteration approvals"],
                  ] as const
                ).map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    className={`${styles.tabButton} ${
                      approvalTab === value ? styles.tabButtonActive : ""
                    }`}
                    onClick={() => {
                      setApprovalTab(value);
                      setSelectedLotStatus(undefined);
                      setSearchQuery("");
                    }}
                  >
                    {label}
                  </button>
                ))}
              </nav>
              <nav className={styles.tabList} aria-label="Approval status">
                {statusFilters.map((filter) => {
                  const isActive = selectedLotStatus === filter.value;
                  return (
                    <button
                      key={filter.label}
                      type="button"
                      className={`${styles.tabButton} ${
                        isActive ? styles.tabButtonActive : ""
                      }`}
                      onClick={() => setSelectedLotStatus(filter.value)}
                    >
                      {filter.label}
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>
        )}
        scroll={{ x: "max-content" }}
        pagination={{
          current: paegNo,
          pageSize: pageSize,
          total: approvalTab === "payment" ? totalCount : alterationTotalCount,
          onChange: handlePaginationChange,
        }}
      />

      <Modal
        open={modalProps.open}
        onCancel={() =>
          setModalProps({ open: false, lotId: "", isView: false })
        }
        footer={null}
        destroyOnClose
        width={1500}
      >
        <LotView
          lotId={modalProps.lotId}
          onClose={() =>
            setModalProps({ open: false, lotId: "", isView: false })
          }
        />
      </Modal>

      {selectedAlteration && (
        <AdminCampaignDetailsChangeModal
          isOpen={alterationModalOpen}
          onClose={() => {
            setAlterationModalOpen(false);
            setSelectedAlteration(null);
          }}
          viewOnly={true}
          alterationRequest={selectedAlteration}
          campaignInfluencerId={selectedAlteration.campaignInfluencerId}
          briefId={selectedAlteration.briefId}
          currentDeliverables={[]}
        />
      )}
    </div>
  );
}

